/**
 * Comprime uma imagem para otimizar o armazenamento.
 * Suporta HEIC/HEIF convertendo para JPEG antes de comprimir.
 *
 * Requisitos: npm i heic2any
 */
export async function compressImage(
  inputFile: File,
  maxWidth: number = 1200,
  maxHeight: number = 1200,
  quality: number = 0.8
): Promise<File> {
  // 1) Detecta HEIC/HEIF por MIME ou extensão
  const isHeicLike = (f: File) => {
    const mt = (f.type || "").toLowerCase();
    const name = (f.name || "").toLowerCase();
    return (
      mt === "image/heic" ||
      mt === "image/heif" ||
      name.endsWith(".heic") ||
      name.endsWith(".heif")
    );
  };

  let file = inputFile;

  // 2) Se for HEIC/HEIF, converte para JPEG usando heic2any
  if (isHeicLike(inputFile)) {
    try {
      const heic2any = (await import("heic2any")).default;
      const convertedBlob = (await heic2any({
        blob: inputFile,
        toType: "image/jpeg",
        quality, // qualidade da conversão inicial
      })) as Blob;

      // renomeia extensão -> .jpg
      const newName = inputFile.name.replace(/\.(heic|heif)$/i, ".jpg");
      file = new File([convertedBlob], newName, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });
    } catch (err) {
      // Se a lib não estiver instalada ou ocorrer erro, falha explícita
      throw new Error(
        "Não foi possível converter HEIC/HEIF. Confirme que 'heic2any' está instalado."
      );
    }
  }

  // 3) Lê a imagem (agora em formato decodificável pelo navegador)
  const dataUrl = await readAsDataURL(file);
  const img = await loadImage(dataUrl);

  // 4) Calcula dimensões mantendo proporção
  const { width, height } = computeFit(
    img.width,
    img.height,
    maxWidth,
    maxHeight
  );

  // 5) Desenha no canvas
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Não foi possível obter contexto do canvas");
  ctx.drawImage(img, 0, 0, width, height);

  // 6) Saída em WEBP (se suportado) ou JPEG
  const outMime = canvasSupportsType("image/webp")
    ? "image/webp"
    : "image/jpeg";
  const outBlob = await canvasToBlob(canvas, outMime, quality);
  if (!outBlob) throw new Error("Falha ao criar blob da imagem");

  // Mantém um nome coerente com o tipo de saída
  const outExt = outMime === "image/webp" ? ".webp" : ".jpg";
  const safeName = sanitizeName(file.name).replace(/\.[^.]+$/, outExt);

  return new File([outBlob], safeName, {
    type: outMime,
    lastModified: Date.now(),
  });
}

/* ------------ helpers internos ------------- */

function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result as string);
    fr.onerror = () => reject(new Error("Falha ao ler o arquivo"));
    fr.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Evita problemas de CORS quando abrir dataURL/objectURL
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Falha ao carregar a imagem"));
    img.src = src;
  });
}

function computeFit(
  w: number,
  h: number,
  maxW: number,
  maxH: number
): { width: number; height: number } {
  let width = w;
  let height = h;
  if (width > height) {
    if (width > maxW) {
      height = Math.round((height * maxW) / width);
      width = maxW;
    }
  } else {
    if (height > maxH) {
      width = Math.round((width * maxH) / height);
      height = maxH;
    }
  }
  return { width, height };
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

function canvasSupportsType(type: string): boolean {
  // Safari antigo pode retornar image/webp como não suportado
  try {
    const c = document.createElement("canvas");
    return c.toDataURL(type).startsWith(`data:${type}`);
  } catch {
    return false;
  }
}

function sanitizeName(name: string): string {
  // remove caracteres problemáticos
  return name.replace(/[^\w.\-()+\s]/g, "_");
}
